import { type FrameName } from "@/game/entities/config";
import { ResourceType } from "@/game/entities/config/resources";
import type { UpgradeIds } from "@/game/entities/config/upgrades";
import { type ClaimablePack } from "@/libs/SocketService";
import { type Rewards as ClaimableReward } from "@/types";
import { EXPLORATION_SPEED_CONST } from "@/types/quest";
import type { SkinRule } from "@/types/shop";
import { CommodityGroup, OfferComplementaryReward } from "@/types/shop";
import { EventPassSkin, ProvisionSkin } from "./shop/skins";

// data logic to front-end conversion
export const resourceTypeToCurrencyType = (
	resourceType: ResourceType | typeof EXPLORATION_SPEED_CONST,
): ResourceType => {
	switch (resourceType) {
		case ResourceType.COINS:
		case ResourceType.COINS_INCOME:
			return ResourceType.COINS;

		case ResourceType.SUPER_CLICKER:
			return ResourceType.SUPER_CLICKER;

		case ResourceType.FOOD_DIFF:
		case ResourceType.FOOD_INCOME:
		case ResourceType.FOOD_CONSUMPTION:
			return ResourceType.FOOD_DIFF;

		case ResourceType.POPULATION_DIFF:
		case ResourceType.POPULATION_INCOME:
		case ResourceType.POPULATION_CONSUMPTION:
			return ResourceType.POPULATION_DIFF;

		case ResourceType.GEMS:
			return ResourceType.GEMS;

		case ResourceType.LUMBER:
		case ResourceType.LUMBER_INCOME:
		case ResourceType.LUMBER_CONSUMPTION:
			return ResourceType.LUMBER;

		case ResourceType.STONE:
		case ResourceType.STONE_INCOME:
		case ResourceType.STONE_CONSUMPTION:
			return ResourceType.STONE;

		case ResourceType.CLAY:
		case ResourceType.CLAY_INCOME:
		case ResourceType.CLAY_CONSUMPTION:
			return ResourceType.CLAY;

		case ResourceType.IRON:
		case ResourceType.IRON_INCOME:
		case ResourceType.IRON_CONSUMPTION:
			return ResourceType.IRON;

		case ResourceType.GOODS:
		case ResourceType.GOODS_INCOME:
		case ResourceType.GOODS_CONSUMPTION:
			return ResourceType.GOODS;

		case ResourceType.STEEL:
		case ResourceType.STEEL_INCOME:
		case ResourceType.STEEL_CONSUMPTION:
			return ResourceType.STEEL;

		case ResourceType.ARMY:
		case ResourceType.ARMY_INCOME:
		case ResourceType.ARMY_CONSUMPTION:
			return ResourceType.ARMY;

		case EXPLORATION_SPEED_CONST:
		case ResourceType.EXPLORATION_SPEED:
			return ResourceType.EXPLORATION_SPEED;
		case ResourceType.AD_SKIPPER:
			return ResourceType.AD_SKIPPER;

		case ResourceType.EVENTIUM:
		case ResourceType.EVENTIUM_INCOME:
			return ResourceType.EVENTIUM;
		case ResourceType.RATIONS:
		case ResourceType.RATIONS_INCOME:
		case ResourceType.RATIONS_CONSUMPTION:
			return ResourceType.RATIONS;
		default:
			return ResourceType.COINS;
	}
};

// Helper function dedicated purely to matching a skin to its complementary reward type
const getMatchingSkinReward = (
	skin: string,
): OfferComplementaryReward | undefined => {
	const skinRules: Array<SkinRule> = [
		{ enums: EventPassSkin, reward: OfferComplementaryReward.EVENT_PASS },
		{ enums: ProvisionSkin, reward: OfferComplementaryReward.PROVISION },
	];

	return skinRules.find((rule: SkinRule): boolean =>
		(Object.values(rule.enums) as Array<string>).includes(skin),
	)?.reward;
};

export const aggregateClaimableRewardsFromPack = (
	pack: ClaimablePack,
): ClaimableReward => {
	const rewards: ClaimableReward = {};

	const addReward = (
		key: ResourceType | FrameName | UpgradeIds | OfferComplementaryReward,
		value: number,
	): void => {
		if (value <= 0) return;
		rewards[key] = (rewards[key] || 0) + value;
	};

	for (const slot of pack.attributes.slots ?? []) {
		for (const item of slot.items ?? []) {
			switch (item.group) {
				case CommodityGroup.BUILDING:
					addReward(item.frame_name, item.qty);
					break;

				case CommodityGroup.RESOURCE:
				case CommodityGroup.UTILITY:
				case CommodityGroup.EVENT_RESOURCE:
					addReward(item.key as ResourceType, item.qty);
					break;

				case CommodityGroup.UPGRADE:
					addReward(item.upgrade_id, item.qty);
					break;
			}
		}
	}

	const skin = pack.attributes?.skin;

	if (skin) {
		const matchingReward = getMatchingSkinReward(skin);

		// NOTE: Only one complementary reward per offer when slots are absent
		if (matchingReward) {
			addReward(matchingReward, 1);
		}
	}

	return rewards;
};

// TODO premenovat
export const formatPrice = (
	price: Partial<Record<ResourceType, number>>,
): Partial<Record<ResourceType, number>> => {
	return (Object.entries(price) as Array<[ResourceType, number]>).reduce(
		(
			acc: Partial<Record<ResourceType, number>>,
			[key, value]: [ResourceType, number],
		): Partial<Record<ResourceType, number>> => {
			// Niektore entity maju rewardu ako callback. Tie by sa sem nemali dostat, tak pre istotu tato podmienka.
			if (typeof value === "number") acc[key] = value;
			return acc;
		},
		{} as Partial<Record<ResourceType, number>>,
	);
};
